import { useEffect, useState } from 'react';
import { ToastSwal } from '../../toastSwals/toastSwal';

export const useDropZoneField = (props: {
    accept: string;
    currentFiles?: { id: number; file: File }[];
    onChange?: (files: File[]) => void;
    maxFileSize?: number;
}) => {
    const { accept, currentFiles, onChange, maxFileSize } = props;
    const [files, setFiles] = useState<{ id: number; file: any }[]>([]);
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
    const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);

    const getAcceptedExtensions = () =>
        accept?.split(',').map((ext) => ext.trim().toLowerCase()) || [];

    const isFileAccepted = (fileName: string) => {
        const fileExtension = `.${fileName.split('.').pop()?.toLowerCase()}`;
        return getAcceptedExtensions().includes(fileExtension);
    };

    const isFileSizeValid = (fileSize: number) => {
        return maxFileSize ? fileSize <= maxFileSize : true;
    };

    const updateFiles = (incomingFiles: any[]) => {
        const validFiles:
            | any[]
            | ((
                  prevState: { id: number; file: any }[]
              ) => { id: number; file: any }[]) = [];
        const invalidFiles: { file: any; type: string }[] = [];

        incomingFiles.forEach((file) => {
            if (!isFileAccepted(file.file.name)) {
                invalidFiles.push({
                    file: file.file.name,
                    type: 'file-invalid-type',
                });
            } else if (!isFileSizeValid(file.file.size)) {
                invalidFiles.push({
                    file: file.file.name,
                    type: 'file-too-large',
                });
            } else {
                validFiles.push(file);
            }
        });
        invalidFiles.forEach((invalid) => {
            if (invalid.type === 'file-invalid-type') {
                ToastSwal.fire({
                    text: `El archivo "${invalid.file}" tiene un tipo no permitido.`,
                    icon: 'error',
                    timer: 20000,
                    position: 'center-end',
                });
            } else if (invalid.type === 'file-too-large') {
                ToastSwal.fire({
                    text: `El archivo "${invalid.file}" es demasiado grande.`,
                    icon: 'error',
                    timer: 20000,
                    position: 'center-end',
                });
            } else {
                ToastSwal.fire({
                    text: `Error desconocido al cargar el archivo "${invalid.file}".`,
                    icon: 'error',
                    timer: 20000,
                    position: 'center-end',
                });
            }
        });

        setFiles(validFiles);
        if (onChange) {
            onChange(validFiles.map((f) => f.file));
        }
    };

    const removeFile = (id: number) => {
        const updatedFiles = files.filter((file) => file.id !== id);
        setFiles(updatedFiles);
        if (onChange) {
            onChange(updatedFiles.map((f) => f.file));
        }
    };

    const handleSee = (imageSource: string | undefined) => {
        setImageSrc(imageSource);
    };

    const handleWatch = (videoSource: string | undefined) => {
        setVideoSrc(videoSource);
    };

    useEffect(() => {
        if (currentFiles) {
            setFiles(currentFiles.map((file, index) => ({ id: index, file })));
        } else {
            setFiles([]);
        }
    }, [currentFiles]);

    return {
        files,
        setFiles,
        imageSrc,
        videoSrc,
        updateFiles,
        removeFile,
        handleSee,
        handleWatch,
        setImageSrc,
        setVideoSrc,
    };
};
